import SlideNavigatorPanel from "./SlideNavigatorPanel";
import PresentationEditorController from "../../PresentationEditor/PresentationEditorController";
import { app } from "../../../namespaces";
import _ from "lodash";
import { ShowDialog, ShowUpgradeDialog } from "../../../react/components/Dialogs/BaseDialog";
import ProgressDialog from "../../../react/components/Dialogs/ProgressDialog";
import { DEFAULT_PROGRESS_DURATION_MS } from "../../../../common/constants";
import WorkspaceController from "../../../controllers/WorkspaceController";
import { ActionPermissionsObject } from "../../../../common/interfaces/index";
import { ExportToPPT } from "../../../exporter/exportToPPT";
import { UpgradePlanDialogType } from "../../../react/views/MarketingDialogs/UpgradePlanDialog";
import { ClipboardController } from "../../PresentationEditor/ClipboardController";
import { getCanvasBundle } from "../../../canvas/index";
import { appVersion } from "../../../config";
import { trackActivity } from "../../../core/utilities/utilities";

export class SlideActionsController {
    static deleteSlides(slidesToDelete) {
        const slides = PresentationEditorController.slides;

        let deleteSlides = _.clone(slidesToDelete);
        // if trying to delete all slides, keep first slide
        if (deleteSlides.length === slides.length) {
            deleteSlides = deleteSlides.slice(1);
        }

        // const firstSlideIndex = deleteSlides[0].getIndex();

        app.undoManager.openGroup();

        let progressDialog = null;
        const timeoutId = setTimeout(() => {
            progressDialog = ShowDialog(ProgressDialog, {
                title: `Deleting slides...`,
            });
        }, DEFAULT_PROGRESS_DURATION_MS);

        PresentationEditorController.presentation.destroySlides(deleteSlides.map(slide => slide.id)).then(() => {
            app.undoManager.closeGroup();

            // use the passedSlide reference so we use the correct slide
            // PresentationEditorController.setCurrentSlide(slides[Math.max(0, firstSlideIndex - 1)].id);

            clearTimeout(timeoutId);
            if (progressDialog) {
                progressDialog.close();
            }
        });
    }

    static async exportSlideToPPT(slideId) {
        const workspaceId = PresentationEditorController.presentation.getWorkspaceId();
        if (WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE].use) {
            const exporter = new ExportToPPT();

            const canvasController = PresentationEditorController.getCanvasControllerForSlide({ id: slideId });
            await canvasController.renderCanvas();

            exporter.export({
                slideCanvases: [canvasController.canvas],
                includeSkippedSlides: true
            });
        } else if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE, "use")) {
            ShowUpgradeDialog({
                type: UpgradePlanDialogType.UPGRADE_PLAN,
                analytics: { cta: "exportToPPT", ...PresentationEditorController.presentation.getAnalytics() },
                workspaceId
            });
        }
    }

    static async skipSlide(slides, isSkipped) {
        await PresentationEditorController.batchSkipSlides(slides, isSkipped);
    }

    static async pasteSlides(selectedSlides, event) {
        // const slides = PresentationEditorController.slides;
        //
        // const filteredSlideIndices = slides
        //     .filter(slide => selectedSlides.includes(slide.id))
        //     .map(slide => slide.getIndex());
        // const insertIndex = (filteredSlideIndices.length > 0
        //     ? _.max(filteredSlideIndices) : slides.length - 1) + 1;

        const insertIndex = _.max(selectedSlides.map(slide => slide.getIndex())) + 1;

        const slideModels = await ClipboardController.pasteSlides(event, insertIndex);
        if (slideModels?.length) {
            PresentationEditorController.setCurrentSlide(slideModels[0].id);
        }
    }

    static async cutSlides(slides) {
        const currentSlide = PresentationEditorController.currentSlide;

        if (!slides || !slides.length) return;
        const presentation = PresentationEditorController.presentation;
        if (!presentation) return;

        const firstSlideIndex = slides[0].getIndex();

        // const targetSlides = PresentationEditorController.slides.filter(slide => selectedSlides.has(slide.id));

        const sortedSlides = presentation.sortSlides(slides, slide => slide.id);

        app.undoManager.openGroup();
        await presentation.cutSlides(sortedSlides);
        app.undoManager.closeGroup();

        // if (currentSlide.id && selectedSlides.has(currentSlide.id)) {
        //     PresentationEditorController.setCurrentSlide(sortedSlides[Math.max(0, firstSlideIndex - 1)].id);
        // }
    }

    static copySlides(slides) {
        const sortedSlides = PresentationEditorController.presentation.sortSlides(slides, slide => slide.id);
        PresentationEditorController.presentation.copySlides(sortedSlides);
    }

    static async duplicateSlides(slides) {
        const sortedSlides = PresentationEditorController.presentation.sortSlides(_.map(slides, slide => slide), slide => slide.id);
        const insertIndex = _.max(slides.map(slideModel => slideModel.getIndex())) + 1;

        app.undoManager.openGroup();
        const slideModels = await PresentationEditorController.presentation.batchDuplicateSlides(sortedSlides, { insertIndex });
        app.undoManager.closeGroup();

        await PresentationEditorController.presentation.load();

        PresentationEditorController.setCurrentSlide(slideModels[0].id);

        const duplicateSlideCount = slideModels.length;
        const { slideTemplates: { slideTemplates } } = await getCanvasBundle(appVersion);
        const eventProps = {
            "slide_ids": slideModels.map(s => s.get("id")),
            "source_slides": slides.map(s => s.get("id")),
            "slide_template_names": slideModels.map(s => slideTemplates[s.get("template_id")].title),
            "slides_created": duplicateSlideCount,
        };
        trackActivity("Slide", "Duplicate", null, duplicateSlideCount, eventProps, { audit: true });
    }
}
